import { FulfillmentOption } from 'common/Constants';
import { ClientEvents } from 'common/events/ClientEvents';
import { formatCurrency } from 'common/FormatUtilities';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Play from 'src/assets/img/play.png';
import compostableTag from 'src/assets/svg/order/compostable.svg';
import { CuisineRegion, OrderShefFragment } from 'src/gqlReactTypings.generated.d';
import { PromoCodeChip, SpecialOfferChips } from 'src/pages/consumer/components/SpecialOfferChips';
import { FollowButton } from 'src/pages/consumer/follow-shef';
import { DescriptionCardsRow } from 'src/pages/consumer/order/components/ShefDescriptionCards/DescriptionCardsRow';
import { ShefSocialIconsRow } from 'src/pages/consumer/order/components/ShefSocialIconsRow';
import { Divider } from 'src/shared/design-system/Atoms';
import { NewShefBadge } from 'src/shared/design-system/Badge';
import { getTopKCuisines } from 'src/shared/design-system/DishFirstCard/utils';
import { generateThumborImgUrl } from 'src/shared/design-system/DynamicImage';
import { ExpandableText } from 'src/shared/design-system/ExpandableText';
import { FaMapMarkerAlt } from 'src/shared/design-system/FontAwesome';
import { Container } from 'src/shared/design-system/Layout';
import { Paragraph } from 'src/shared/design-system/Text';
import { useGoogleMapsScriptTag } from 'src/shared/hooks/useGoogleMapsScriptTag';
import { useTracker } from 'src/shared/hooks/useTracker';
import { maxScreen, minScreen } from 'src/shared/utils/StyledComponentUtils';
import { ZIndexes } from 'src/shared/ZIndexes';
import { TopShefFlag } from 'src/top-shef/TopShefFlag';
import { TopShefInfoModal } from 'src/top-shef/TopShefInfoModal';
import { lazyRetry } from 'src/util/retry';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';
import { Colors, ScreenWidth } from 'web-common/src/shared/Constants';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ifNotProp } from 'web-common/src/shared/styled-utils/ifNotProp';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Font, Spacing } from 'web-common/src/shared/styles';
import { useScopedReducer } from '../../../../shef-global-state/shefGlobalState';
import { shouldShowNewLabel } from '../../components/ConsumerShefRating';
import { SHEF_IMAGE_SMALL_SCREEN_BORDER_WIDTH } from '../../components/ShefImage';
import { ConsumerOrderGoogleMap } from './ConsumerOrderGoogleMap';
import { ConsumerOrderShefImage } from './ConsumerOrderShefImage';
import { MessageShefButton } from './MessageShefButton';
import { IRadioButtonGroupProps, RadioButtonGroup } from './radio-button-group';
import { ReferShefButton } from './ReferShefButton';

const VideoModal = lazyRetry(() => import('src/shared/design-system/VideoModal'));

interface IProps {
  shef: OrderShefFragment;
  foodItemImageUrl: string;
  finalDeliveryPrice?: number;
  radioButtonProps: IRadioButtonGroupProps;
  hasMultipleMenus: boolean;
  hasCompostableItems: boolean;
  hasHiddenFoodItems?: boolean;
}

const BootstrapMaxWidth = '1140px';

const ConsumerOrderShefVideoDetailsContainer = styled.div`
  /* background-color: white; */
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${BootstrapMaxWidth};
  margin-bottom: 30px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    margin-bottom: 15px;
  }
`;

interface ICoverProps {
  isVideo: boolean;
  canRoundCorners: boolean;
  src: string;
  alt: string;
}

const PlayButton = styled.div`
  transition: all 0.25s cubic-bezier(1, 1.66, 0.7, 0.77);
  position: absolute;
  background: url('${generateThumborImgUrl({ src: Play, width: 46, height: 54 })}');
  height: 54px;
  width: 46px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const ShefCoverImg = styled.div<ICoverProps>`
  z-index: 0;
  position: relative;
  width: 100%;
  height: 200px;
  margin-bottom: 35px;
  border-radius: ${({ canRoundCorners }) => (canRoundCorners ? '0 0 10px 10px' : '0')};
  background-size: cover;
  background-position: center;
  background-color: ${Colors.GRAY_LITE_30};
  background-image: url('${({ src }) => src}');
  cursor: ${({ isVideo }) => (isVideo ? 'pointer' : 'unset')};

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover ${PlayButton} {
    height: 70px;
    width: 70px;
  }

  @media (max-width: ${ScreenWidth.SMALL}) {
    height: ${({ isVideo }) => (isVideo ? 223 : 160)}px;
    margin-bottom: 0;
  }

  @media (max-width: ${BootstrapMaxWidth}) {
    border-radius: 0;
  }
`;

const ShefSpecialtyLine = styled.div`
  display: flex;
  font-family: ${Font.Family.SANS};
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.02em;
  margin-bottom: 10px;
`;

const ShefSocialIconsRowDesktop = styled(ShefSocialIconsRow)`
  width: 100%;
  justify-content: flex-end;
  margin-top: ${Spacing.HALF};
`;

const ShefSocialIconsRowMobile = styled(ShefSocialIconsRow)`
  margin-bottom: ${Spacing.HALF};
`;

const ShefBio = styled.div`
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.MEDIUM};
  line-height: 19px;
  letter-spacing: 0.02em;
  color: ${Colors.GRAY_DARK_20};
  margin-right: 60px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    margin: 0;
  }
`;

const ShefInfoRow = styled.div`
  z-index: ${ZIndexes.ShefInfoContainer};
  display: flex;
  flex-direction: row;
  margin-right: 15px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    align-items: center;
    flex-direction: column;
    margin-right: 0;
  }
`;

const ShefBioContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  position: relative;
  @media (max-width: ${ScreenWidth.SMALL}) {
    align-items: flex-start;
    width: 100%;
  }
`;
const ShefBioPadding = styled.div`
  @media (max-width: ${ScreenWidth.SMALL}) {
    padding: ${Spacing.NONE} ${Spacing.HALF} ${Spacing.NONE} ${Spacing.SINGLE};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled.div<{
  showNewShefLabel: boolean;
}>`
  font-family: ${Font.Family.SANS};
  font-weight: 500;
  font-size: 32px;
  line-height: 32px;
  margin-bottom: 10px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    font-size: 26px;
    line-height: 28px;
    margin-top: ${Spacing.SINGLE};
  }
`;

const SubTitle = styled.div`
  font-family: ${Font.Family.BODY_SANS};
  font-weight: ${Font.Weight.NORMAL};
  font-size: ${Font.Size.S};
  color: ${Colors.GRAY_DARK_20};
  margin-top: -${Spacing.THREE_QUARTERS};
  margin-bottom: ${Spacing.HALF};

  ${minScreen(ScreenWidth.SMALL)} {
    margin-top: -${Spacing.QUARTER};
    font-weight: ${Font.Weight.MEDIUM};
  }
`;

const ShefActionsContainerDesktop = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: ${Spacing.SINGLE};
  gap: ${Spacing.HALF};
`;
const ShefActionButtonsRowDesktop = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: flex-end;
  gap: ${Spacing.HALF};
`;

const ShefActionsContainerMobile = styled.div`
  position: absolute;
  left: ${Spacing.THREE_QUARTERS};
  bottom: ${Spacing.THREE_QUARTERS};
  display: flex;
  gap: ${Spacing.HALF};
`;

const RadioButtons = styled(RadioButtonGroup)<{ show: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  ${ifNotProp(
    'show',
    css`
      visibility: hidden;
    `
  )}
`;

const LowButtonsRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin-top: 15px;
`;

const ShefImageInnerContainer = styled.div`
  margin: 0 25px;
  position: relative;
  height: min-content;

  @media (max-width: ${ScreenWidth.SMALL}) {
    margin: -60px 0 0 ${Spacing.HALF};
  }
`;

const ShefImageOuterContainer = styled.div`
  display: flex;
  align-self: flex-end;
  height: 100%;

  @media (min-width: ${ScreenWidth.SMALL}) {
    display: flex;
    align-self: flex-start;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;
  left: ${Spacing.NONE};
  right: ${Spacing.NONE};
  margin: auto ${Spacing.NONE};
  bottom: -10px;
  text-align: center;
  @media (max-width: ${ScreenWidth.SMALL}) {
    bottom: ${-10 + SHEF_IMAGE_SMALL_SCREEN_BORDER_WIDTH}px;
  }
`;

const CompostableLabel = styled.img`
  background-color: ${Colors.WHITE};
  border: 3px solid ${Colors.WHITE};
  border-radius: 15px;
  width: 24px;
  height: 24px;
  bottom: ${Spacing.THREE_QUARTERS};
  right: ${Spacing.THREE_QUARTERS};
  object-fit: fill;
  position: absolute;
  box-sizing: content-box;
  @media (max-width: ${ScreenWidth.SMALL}) {
    bottom: ${Spacing.HALF};
    right: ${Spacing.HALF};
  }
`;

const DividerContainer = styled(Divider)`
  display: none;

  ${minScreen(ScreenWidth.SMALL)} {
    display: block;
  }
`;

const PickUpDetailsSection = styled(Container)<{ noPadding: boolean }>`
  ${ifProp(
    'noPadding',
    css`
      padding: 0;
    `
  )}
`;

const PickUpOnlyMessage = styled(Paragraph)`
  border-radius: 0 0 8px 8px;
  border: 1px solid ${Colors.GRAY_LITE_10};
  border-top: none;
  margin-bottom: 5px;
  padding: 13px 0;
  text-align: center;

  ${minScreen(ScreenWidth.SMALL)} {
    border: none;
    margin: 0;
    padding: 10px 0;
  }
`;

const GoogleMapWithPickUpOnlyMessageContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${minScreen(ScreenWidth.SMALL)} {
    flex-direction: column-reverse;
  }
`;

const ConsumerOrderGoogleMapContainer = styled(ConsumerOrderGoogleMap)`
  ${maxScreen(ScreenWidth.SMALL)} {
    border-radius: 8px 8px 0 0;
  }
`;

export const ConsumerOrderShefVideoDetails: React.FC<IProps> = ({
  shef,
  foodItemImageUrl,
  finalDeliveryPrice,
  radioButtonProps,
  hasMultipleMenus,
  hasCompostableItems,
  hasHiddenFoodItems = false,
}) => {
  const [showVideo, setShowVideo] = useState<boolean>();
  const { shefProfile, firstName, imageUrl: shefImageUrl } = shef;
  const { nearestScreenWidthValue } = useScreenContext();
  const [canUseMap] = useGoogleMapsScriptTag();
  const { state: configs } = useScopedReducer('configs');

  const isDonationType = shef.shefProfile.cuisineRegion === CuisineRegion.Donations;
  const showDelivery = configs.upfrontDeliveryFeeEnabled && !isNil(finalDeliveryPrice);

  const hasSocialMedia =
    !!shefProfile.websiteUrl ||
    !!shefProfile.facebookHandle ||
    !!shefProfile.instagramHandle ||
    !!shefProfile.twitterHandle ||
    !!shefProfile.tikTokHandle ||
    !!shefProfile.linkedInHandle;

  const rawCoverUrl = useMemo(() => {
    if (hasHiddenFoodItems) {
      return foodItemImageUrl;
    }
    return shefProfile.coverPhotoUrl ?? shefProfile.previewFoodItem?.imageUrl ?? foodItemImageUrl;
  }, [foodItemImageUrl, hasHiddenFoodItems, shefProfile.coverPhotoUrl, shefProfile.previewFoodItem?.imageUrl]);
  const coverUrl = generateThumborImgUrl({ src: rawCoverUrl, width: nearestScreenWidthValue });
  const { approximateLocation, fulfillmentOption } = shef.shefProfile;

  const { isSmallScreen, windowDimensions } = useScreenContext();

  const screenWidth = windowDimensions.width;
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>();
  const parentElement = containerRef?.parentElement;
  const isParentWiderThanContainer = useMemo(() => {
    if (containerRef && parentElement) {
      const { width } = containerRef.getBoundingClientRect();
      const parentWidth = parentElement.getBoundingClientRect().width;
      return parentWidth > width;
    }
    return false;
    // need this to recalculate on resize
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenWidth, containerRef, parentElement]);

  const tracker = useTracker();

  const hasVideo = Boolean(shefProfile.videoUrl);
  const coverImageClick = useCallback(() => {
    if (!hasVideo) {
      return;
    }

    tracker.track(ClientEvents.VIDEO_BIO_CLICK, {
      isCoverImage: true,
      shefId: shef.id,
    });

    setShowVideo(true);
  }, [hasVideo, shef.id, tracker]);

  const learnMoreClick = useCallback(() => {
    tracker.track(ClientEvents.LEARN_MORE_CLICK, {
      shefId: shef.id,
    });
  }, [shef.id, tracker]);

  const showNewShefLabel = shouldShowNewLabel({
    numRatings: shef?.rating?.count ?? 0,
    isTrialShef: shef?.shefProfile?.isTrialShef ?? false,
  });

  const [isTopShefModalShowing, setIsTopShefModalShowing] = useState(false);
  const showTopShefModal = useCallback(() => {
    setIsTopShefModalShowing(true);
  }, []);
  const hideTopShefModal = useCallback(() => {
    setIsTopShefModalShowing(false);
  }, []);

  const showRadioGroup = !isSmallScreen && hasMultipleMenus;
  const titleRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const [showLowButtons, setIsIntersecting] = useState(false);
  const { explorePagePromoCode } = shef.shefProfile;

  if (fulfillmentOption === FulfillmentOption.Pickup && !approximateLocation) {
    console.error(`FulfillmentOption Pickup is selected for shef id (${shef.id}), but approximateLocation is missing.`);
  }

  useEffect(() => {
    if (titleRef.current && buttonsRef.current) {
      const titleRect = titleRef.current.getBoundingClientRect();
      const buttonsRect = buttonsRef.current.getBoundingClientRect();
      setIsIntersecting(buttonsRect.x < titleRect.x + titleRect.width);
      return;
    }

    setIsIntersecting(false);
  }, [windowDimensions]);

  const title = match({ isDonationType, hasBusinessName: !!shefProfile.businessName })
    .with({ isDonationType: true }, () => 'Donation Menu')
    .with({ hasBusinessName: true }, () => shefProfile.businessName)
    .otherwise(() => `Shef ${firstName}'s Menu`);

  return (
    <ConsumerOrderShefVideoDetailsContainer data-cy='shef-discovery-desktop' ref={setContainerRef}>
      <ShefCoverImg
        isVideo={hasVideo}
        canRoundCorners={isParentWiderThanContainer}
        onClick={coverImageClick}
        src={coverUrl}
        alt='Cover Image'>
        {hasVideo && <PlayButton />}
        {explorePagePromoCode && (
          <SpecialOfferChips
            topLeftChip={
              <PromoCodeChip value={explorePagePromoCode.value} isPercentage={explorePagePromoCode.isPercentage} />
            }
          />
        )}
        {isSmallScreen && (
          <ShefActionsContainerMobile>
            <FollowButton smallScreen imageUrl={shefImageUrl} name={shef.publicName} shefId={shef.id} location='menu' />
            <MessageShefButton shef={shef} />
            <ReferShefButton shefId={shef.id} />
          </ShefActionsContainerMobile>
        )}
      </ShefCoverImg>

      <ShefInfoRow>
        {shefImageUrl && !isSmallScreen && (
          <ShefImageOuterContainer>
            <ShefImageInnerContainer>
              <ConsumerOrderShefImage shefImageUrl={shefImageUrl} shefId={shef.id} smallScreen={isSmallScreen} />
              <BadgeContainer>{!hasCompostableItems && showNewShefLabel && <NewShefBadge outline />}</BadgeContainer>
              {hasCompostableItems && <CompostableLabel src={compostableTag} alt='Compostable packaging available' />}
            </ShefImageInnerContainer>
          </ShefImageOuterContainer>
        )}
        <ShefBioContainer>
          <ShefBioPadding>
            <TitleContainer>
              <Title showNewShefLabel={showNewShefLabel} ref={titleRef}>
                {title}
              </Title>

              {shefImageUrl && isSmallScreen && (
                <ShefImageOuterContainer>
                  <ShefImageInnerContainer>
                    <ConsumerOrderShefImage shefImageUrl={shefImageUrl} shefId={shef.id} smallScreen={isSmallScreen} />
                    <BadgeContainer>
                      {!hasCompostableItems && showNewShefLabel && <NewShefBadge outline />}
                    </BadgeContainer>
                    {hasCompostableItems && (
                      <CompostableLabel src={compostableTag} alt='Compostable packaging available' />
                    )}
                  </ShefImageInnerContainer>
                </ShefImageOuterContainer>
              )}
            </TitleContainer>
            {shefProfile.businessName && <SubTitle>By Shef {firstName}</SubTitle>}
            {shef.isTopShef && (
              <>
                <TopShefFlag className='mb-4 inline-flex' onClick={showTopShefModal} />
                <TopShefInfoModal isShowing={isTopShefModalShowing} onClose={hideTopShefModal} />
              </>
            )}
            {showRadioGroup && <RadioButtons ref={buttonsRef} show={!showLowButtons} {...radioButtonProps} />}
            {!shef.celebrityEventType && <ShefSpecialtyLine>{getTopKCuisines({ ...shefProfile })}</ShefSpecialtyLine>}
            {showDelivery ? <span>{formatCurrency(finalDeliveryPrice, true)} Delivery Fee</span> : null}
          </ShefBioPadding>
          {!shef.celebrityEventType && <DescriptionCardsRow shef={shef} />}

          <ShefBioPadding>
            <ShefBio data-cy='shef-discovery-shef-description'>
              {shef.shefProfile.bio && (
                <ExpandableText
                  onClickExpand={learnMoreClick}
                  expandText={'Learn More'}
                  numberOfLines={3}
                  text={shef.shefProfile.bio}
                />
              )}
            </ShefBio>
            {isSmallScreen && hasSocialMedia && <ShefSocialIconsRowMobile shefProfile={shefProfile} />}
          </ShefBioPadding>
        </ShefBioContainer>

        {!isSmallScreen && (
          <ShefActionsContainerDesktop>
            <ShefActionButtonsRowDesktop>
              <FollowButton imageUrl={shefImageUrl} name={shef.publicName} shefId={shef.id} location='menu' />
              <MessageShefButton shef={shef} />
              <ReferShefButton shefId={shef.id} />
            </ShefActionButtonsRowDesktop>
            {hasSocialMedia && <ShefSocialIconsRowDesktop shefProfile={shefProfile} />}
          </ShefActionsContainerDesktop>
        )}
      </ShefInfoRow>
      {fulfillmentOption === FulfillmentOption.Pickup && approximateLocation && canUseMap && (
        <PickUpDetailsSection fullWidth fluid noPadding={isParentWiderThanContainer}>
          <DividerContainer marginBottom={0} />
          <GoogleMapWithPickUpOnlyMessageContainer>
            <ConsumerOrderGoogleMapContainer
              isApproximateLocation
              latitude={approximateLocation.latitude}
              longitude={approximateLocation.longitude}
            />
            <PickUpOnlyMessage color={Colors.RADISH} size={Font.Size.XS} weight={Font.Weight.BOLD}>
              <FaMapMarkerAlt style={{ marginRight: '10px' }} />
              This shef’s food is only available for pickup
            </PickUpOnlyMessage>
          </GoogleMapWithPickUpOnlyMessageContainer>
        </PickUpDetailsSection>
      )}
      {showLowButtons && (
        <LowButtonsRow>
          <RadioButtonGroup {...radioButtonProps} />
        </LowButtonsRow>
      )}
      {shefProfile.videoUrl && (
        <VideoModal
          show={showVideo}
          url={shefProfile.videoUrl}
          title={`Shef ${firstName}`}
          onHide={() => setShowVideo(false)}
        />
      )}
    </ConsumerOrderShefVideoDetailsContainer>
  );
};
